import { useCallback, useEffect, useState } from "react";
import { api, ApiError, download } from "../api";

/**
 * "File Return" — the one-click path.
 *
 *   1. Readiness      what the return needs that we don't yet have
 *   2. Fill the gaps   scan the taxpayer's own documents for candidates, confirm
 *   3. Export          build + validate against ITD's schema, then download
 *
 * The export button stays disabled while the return has schema errors, so a
 * file that ITD would reject can't be downloaded and mistaken for ready. The
 * candidate list is confirm-only on purpose: a blind scan of bank and demat
 * statements also finds the custodian's email and the bank's own IFSC, and
 * committing those would route a refund to a stranger.
 */

const AYS = ["AY2026-27", "AY2025-26"];

interface Readiness {
  ready: boolean;
  form: string;
  formSelection: { form: string; reasons: string[]; overrides: string[] };
  missingMandatoryFields: string[];
  missingDocuments: string[];
  schemaErrors?: { path: string; keyword: string; message: string }[];
  schemaAvailable?: boolean;
  constantsStatus: string;
}

interface Candidate {
  field: string;
  value: string;
  confidence: number;
  sourceTitle: string;
  sourceDocType: string;
  occurrences: number;
  reason: string;
}

interface ScanResult {
  documentsScanned: number;
  documentsConsidered: number;
  candidates: Record<string, Candidate[]>;
  note: string;
}

const FIELD_LABEL: Record<string, string> = {
  aadhaar: "Aadhaar number",
  email: "Email address",
  mobile: "Mobile number",
  ifsc: "Bank IFSC",
  bankAccount: "Bank account number",
  pan: "PAN",
};

/** Candidate field -> the profile key it writes to. */
const PROFILE_KEY: Record<string, string> = {
  aadhaar: "aadhaar",
  email: "email",
  mobile: "mobile",
  ifsc: "bankIfsc",
  bankAccount: "bankAccount",
  pan: "pan",
};

export default function FileReturn() {
  const [ay, setAy] = useState(AYS[0]);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [scan, setScan] = useState<ScanResult | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState<Record<string, string>>({});

  const loadReadiness = useCallback(async () => {
    setError(null);
    try {
      setReadiness(await api.get<Readiness>(`/api/tax/${ay}/readiness`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "could not load readiness");
    }
  }, [ay]);

  useEffect(() => {
    void loadReadiness();
  }, [loadReadiness]);

  const runScan = async () => {
    setBusy("scan");
    setError(null);
    try {
      setScan(await api.post<ScanResult>("/api/tax/scan-identity", {}));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "scan failed");
    } finally {
      setBusy(null);
    }
  };

  const saveField = async (field: string, value: string) => {
    const key = PROFILE_KEY[field] ?? field;
    setBusy(`save:${field}:${value}`);
    setError(null);
    try {
      await api.put("/api/tax/profile", { [key]: value });
      await loadReadiness();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `could not save ${field}`);
    } finally {
      setBusy(null);
    }
  };

  const exportJson = async () => {
    setBusy("export");
    setError(null);
    try {
      await download(`/api/tax/${ay}/return.json`, `${readiness?.form ?? "ITR"}-${ay}.json`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "export failed");
    } finally {
      setBusy(null);
    }
  };

  const schemaErrors = readiness?.schemaErrors ?? [];
  const blocked = schemaErrors.length > 0;

  return (
    <div style={{ padding: 16, maxWidth: 900 }}>
      <h2 style={{ marginBottom: 4 }}>File return</h2>
      <p style={{ color: "#666", marginTop: 0, fontSize: 13 }}>
        Prepares and validates the return. It does <strong>not</strong> file — you upload the
        validated file and e-verify on the portal.
      </p>

      <label style={{ display: "block", margin: "12px 0" }}>
        Assessment year&nbsp;
        <select value={ay} onChange={(e) => setAy(e.target.value)}>
          {AYS.map((a) => (
            <option key={a}>{a}</option>
          ))}
        </select>
      </label>

      {error && (
        <div style={{ background: "#fdecea", color: "#a3160b", padding: 10, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* ---- 1. Readiness ---- */}
      {readiness && (
        <section style={{ border: "1px solid #ddd", borderRadius: 8, padding: 14, marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>
            {readiness.form}{" "}
            <span style={{ fontSize: 13, fontWeight: 400, color: "#666" }}>— auto-selected</span>
          </h3>
          <ul style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
            {readiness.formSelection?.reasons?.map((r) => (
              <li key={r}>{r}</li>
            ))}
            {readiness.formSelection?.overrides?.map((r) => (
              <li key={r} style={{ color: "#8a5a00" }}>{r}</li>
            ))}
          </ul>

          <div style={{ marginTop: 10 }}>
            <strong>Schema validation:</strong>{" "}
            {readiness.schemaAvailable === false ? (
              <span style={{ color: "#a3160b" }}>no ITD schema for this year — NOT validated</span>
            ) : blocked ? (
              <span style={{ color: "#a3160b" }}>{schemaErrors.length} error(s)</span>
            ) : (
              <span style={{ color: "#0a7d33" }}>clean — 0 errors against ITD's schema</span>
            )}
          </div>
          {blocked && (
            <ul style={{ fontSize: 12, color: "#a3160b", maxHeight: 160, overflow: "auto" }}>
              {schemaErrors.slice(0, 20).map((e, i) => (
                <li key={i}>
                  <code>{e.path}</code> — {e.message}
                </li>
              ))}
            </ul>
          )}

          {readiness.missingMandatoryFields.length > 0 && (
            <>
              <div style={{ marginTop: 10 }}>
                <strong>Missing mandatory fields</strong>
              </div>
              <ul style={{ fontSize: 13, color: "#a3160b" }}>
                {readiness.missingMandatoryFields.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </>
          )}
          {readiness.missingDocuments.length > 0 && (
            <>
              <div style={{ marginTop: 6 }}>
                <strong>Missing documents</strong>
              </div>
              <ul style={{ fontSize: 13, color: "#8a5a00" }}>
                {readiness.missingDocuments.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </>
          )}
        </section>
      )}

      {/* ---- 2. Fill the gaps ---- */}
      <section style={{ border: "1px solid #ddd", borderRadius: 8, padding: 14, marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Fill the gaps</h3>
        <button onClick={runScan} disabled={busy === "scan"}>
          {busy === "scan" ? "Scanning documents…" : "Scan my documents"}
        </button>
        {scan && (
          <>
            <p style={{ fontSize: 12, color: "#666" }}>
              Read {scan.documentsScanned} of {scan.documentsConsidered} documents. {scan.note}
            </p>
            {Object.entries(scan.candidates).map(([field, list]) => (
              <div key={field} style={{ marginTop: 10 }}>
                <strong style={{ fontSize: 13 }}>{FIELD_LABEL[field] ?? field}</strong>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                  <tbody>
                    {list.map((c) => (
                      <tr key={c.value} style={{ borderTop: "1px solid #eee" }}>
                        <td style={{ padding: "4px 6px", fontFamily: "monospace" }}>{c.value}</td>
                        <td style={{ padding: "4px 6px", color: "#666" }}>
                          {c.sourceTitle} ({c.sourceDocType}) ×{c.occurrences}
                        </td>
                        <td style={{ padding: "4px 6px", color: "#888" }}>
                          {Math.round(c.confidence * 100)}%
                        </td>
                        <td style={{ padding: "4px 6px" }}>
                          <button
                            onClick={() => void saveField(field, c.value)}
                            disabled={busy === `save:${field}:${c.value}`}
                          >
                            Use
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </>
        )}

        <div style={{ marginTop: 14, borderTop: "1px solid #eee", paddingTop: 10 }}>
          <strong style={{ fontSize: 13 }}>Or enter directly</strong>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
            {["aadhaar", "email", "mobile", "ifsc", "bankAccount", "fathersName", "bankName"].map((f) => (
              <div key={f} style={{ display: "flex", gap: 4 }}>
                <input
                  placeholder={FIELD_LABEL[f] ?? f}
                  value={manual[f] ?? ""}
                  onChange={(e) => setManual({ ...manual, [f]: e.target.value })}
                  style={{ flex: 1, padding: 4 }}
                />
                <button
                  onClick={() => void saveField(f, manual[f] ?? "")}
                  disabled={!manual[f]}
                >
                  Save
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- 3. Export ---- */}
      <section style={{ border: "1px solid #ddd", borderRadius: 8, padding: 14 }}>
        <h3 style={{ marginTop: 0 }}>Export</h3>
        <button onClick={exportJson} disabled={busy === "export" || blocked} style={{ padding: "8px 16px" }}>
          {busy === "export" ? "Building…" : `Download ${readiness?.form ?? "ITR"} JSON`}
        </button>
        {blocked && (
          <p style={{ color: "#a3160b", fontSize: 13 }}>
            Disabled — the return still has schema errors. Fix them above first.
          </p>
        )}
        <p style={{ fontSize: 12, color: "#666" }}>
          Structural validity is necessary but not sufficient: ITD's separate validation rules
          (cross-field business checks) are not encoded here. Run the official utility's Validate
          step before uploading.
        </p>
      </section>
    </div>
  );
}
